// @ts-strict-ignore
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Arrange, DropdownDivider, Link, NoStyleButton, useToast } from "@aviary";
import { MobileNav } from "@clinic/shared/components/MobileNav/MobileNav";
import { usePlatformRedirectMutation } from "@clinic/shared/data/mutations/PlatformRedirectMutation/platformRedirect.mutation";
import GreenIconSVG from "@emerson/assets/svg/GreenIcon";
import { RsoModal } from "@emerson/components/RsoModal/RsoModal";
import { emersonRoutes } from "@emerson/data/emersonRoutes";
import { useWordpressSpecialsQuery } from "@emerson/data/queries/WordpressSpecialsQuery/WordpressSpecialsQuery.query";
import { l } from "@emerson/locales/i18n";
import { navigateWithoutRouter } from "@helpers/navigateWithoutRouter/navigateWithoutRouter";
import { SvgEmersonLogo } from "@shared/assets/logo";
import { StopImpersonating } from "@shared/components/StopImpersonating/StopImpersonating";
import { useHasMultipleStorePlatformsQuery } from "@shared/data/queries/HasMultipleStorePlatformsQuery/HasMultipleStorePlatforms.query";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { isE2E } from "@shared/utils/fullscriptEnv/fullscriptEnv";

import { EmersonProfileToggle } from "./EmersonProfileToggle/EmersonProfileToggle";
import { EmersonSidebarItems } from "./EmersonSidebarItems/EmersonSidebarItems";
import { MobileNavRightIcons } from "./MobileNavRightIcons/MobileNavRightIcons";
import { SearchModal } from "./SearchModal/SearchModal";
import { useSidebarQueryEmersonQuery } from "./Sidebar.query.generated";
import { Sidebar } from "./Sidebar/Sidebar";

import * as styles from "./EmersonMobileNav.styles";

const EmersonMobileNav = () => {
  const { t } = useTranslation();
  const { makeToast } = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const { isAdminImpersonating, isUnauthenticated } = useSharedGlobalConfig();

  const { data, loading, error } = useSidebarQueryEmersonQuery({
    skip: !isOpen || isUnauthenticated,
  });

  const [isRsoModalOpen, setIsRsoModalOpen] = useState(false);

  const { currentPractitioner } = data ?? {};
  const { data: officeData } = useHasMultipleStorePlatformsQuery({ skip: isUnauthenticated });
  const { hasMultipleStorePlatforms } = officeData?.currentOffice?.user ?? {};
  const logAnalyticsEvent = useAnalyticsLogger();

  const [requestRedirectUrl] = usePlatformRedirectMutation({
    onError: () => {
      makeToast("error", t(l.rso.errorRedirect));
    },
  });

  const { data: currentSpecials, loading: loadingSpecials } = useWordpressSpecialsQuery({
    skip: isE2E(),
  });
  const showSpecials = !loadingSpecials && currentSpecials?.specials?.nodes.length > 0;
  const specials = !loadingSpecials ? currentSpecials?.specials?.nodes : [];

  const exploreFullscript = async () => {
    const { data: redirectData, errors: redirectError } = await requestRedirectUrl({
      variables: {
        input: {
          targetPlatformName: "FULLSCRIPT",
        },
      },
    });

    if (redirectError || redirectData.platformRedirect.errors.message) {
      makeToast("error", t(l.rso.errorRedirect));

      return;
    }

    void logAnalyticsEvent({
      eventName: AnalyticsEvent.EMERSON_ACCOUNT_MENU_SWITCH_PLATFORM,
      payload: {},
    });

    navigateWithoutRouter(redirectData?.platformRedirect?.redirectPath);
  };

  const renderStopImpersonating = () => {
    if (isAdminImpersonating) {
      return <StopImpersonating css={styles.stopImpersonatingLink} isText />;
    }

    return null;
  };

  const renderRsoLink = () => {
    if (isUnauthenticated || isAdminImpersonating) {
      return null;
    }

    if (hasMultipleStorePlatforms)
      return (
        <>
          <NoStyleButton
            onClick={exploreFullscript}
            data-testid="rso-bridge-mobile-button"
            css={styles.rsoButton}
          >
            <GreenIconSVG />
            {t(l.header.Account.WritePlans)}
          </NoStyleButton>
          <DropdownDivider />
        </>
      );

    return (
      <>
        <NoStyleButton
          onClick={() => setIsRsoModalOpen(!isRsoModalOpen)}
          data-testid="rso-bridge-mobile-button"
          css={styles.rsoButton}
        >
          <GreenIconSVG />
          {t(l.header.Account.WritePlans)}
        </NoStyleButton>
        <DropdownDivider />
      </>
    );
  };

  const renderAccountProfileToggle = () => {
    if (isUnauthenticated || error || loading) return null;

    return (
      <div css={styles.profileToggle}>
        <EmersonProfileToggle
          firstName={currentPractitioner?.firstName}
          lastName={currentPractitioner?.lastName}
          avatarForText={t("AvatarFor")}
          signOutText={t("SignOut")}
          t={t}
        >
          <Arrange isVertical isFullWidth>
            <Link
              css={styles.profileToggleLink}
              isUnderLined={false}
              isHoverUnderLined={false}
              to={emersonRoutes.account.basicInfo}
              onClick={handleOnClose}
            >
              {t(l.header.Account.AccountSettings)}
            </Link>
            <Link
              css={styles.profileToggleLink}
              isUnderLined={false}
              isHoverUnderLined={false}
              to={emersonRoutes.orders.orderHistory}
              onClick={handleOnClose}
            >
              {t(l.header.Account.MyOrders)}
            </Link>
            <Link
              css={styles.profileToggleLink}
              isUnderLined={false}
              isHoverUnderLined={false}
              href={emersonRoutes.external.support}
              onClick={handleOnClose}
              noExternalIcon
            >
              {t(l.header.Account.Support)}
            </Link>
            {renderStopImpersonating()}
            <DropdownDivider />
            {renderRsoLink()}
          </Arrange>
        </EmersonProfileToggle>
      </div>
    );
  };

  const toggleSearchModal = () => setIsSearchOpen(!isSearchOpen);

  const toggleOpen = () => setIsOpen(!isOpen);
  const handleOnClose = () => setIsOpen(false);

  return (
    <div css={styles.mobileNavWrapper}>
      <RsoModal
        hasMultipleStorePlatforms={hasMultipleStorePlatforms}
        isOpen={isRsoModalOpen}
        onClose={() => setIsRsoModalOpen(!isRsoModalOpen)}
      />
      <MobileNav
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        homeRoute={emersonRoutes.base}
        homeButton={<MobileNavRightIcons toggleSearchModal={toggleSearchModal} />}
        logo={<SvgEmersonLogo css={styles.logo} role="img" aria-label={t(l.header.Logo)} />}
      >
        <EmersonSidebarItems isOpen={isOpen} onClose={handleOnClose}>
          <Sidebar onClose={handleOnClose} specialsData={{ specials, showSpecials }} />
          {renderAccountProfileToggle()}
        </EmersonSidebarItems>
      </MobileNav>
      <SearchModal isOpen={isSearchOpen} onClose={toggleSearchModal} />
    </div>
  );
};

export { EmersonMobileNav };
